import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Mic, Send, Square, Trash2, Volume2, Paperclip, X, Loader2 } from "lucide-react";

const Waveform = () => (
  <div className="flex items-end gap-[3px] h-5" aria-hidden>
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} className="wave-bar" style={{ animationDelay: `${i * 0.12}s` }} />
    ))}
  </div>
);

export default function StudioPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [attach, setAttach] = useState(null); // {b64, preview}
  const [speakingId, setSpeakingId] = useState(null);

  const feedRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    api.getHistory().then(setMessages).catch(() => {});
  }, []);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [messages, sending, processing]);

  const playB64 = (b64, id) => {
    try {
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      audioRef.current = audio;
      setSpeakingId(id);
      audio.onended = () => setSpeakingId(null);
      audio.play();
    } catch (e) {
      setSpeakingId(null);
    }
  };

  const handleSend = async () => {
    if ((!input.trim() && !attach) || sending) return;
    const text = input.trim() || "Here's a reference — what do you see?";
    setInput("");
    setSending(true);
    const img = attach?.b64 || null;
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", text, image_base64: attach?.preview }]);
    setAttach(null);
    try {
      const res = await api.sendChat(text, img);
      setMessages((m) => [...m.filter((x) => !String(x.id).startsWith("tmp-")), res.user, res.assistant]);
    } catch (e) {
      toast.error("ATELIER could not respond", { description: e?.response?.data?.detail || "Try again." });
      setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
    } finally {
      setSending(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = String(dataUrl).split(",")[1];
      setAttach({ b64, preview: dataUrl });
    };
    reader.readAsDataURL(f);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size > 0 && chunksRef.current.push(ev.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setProcessing(true);
        try {
          const res = await api.voiceChat(blob);
          setMessages((m) => [...m, res.user, res.assistant]);
          if (res.audio_base64) playB64(res.audio_base64, res.assistant.id);
        } catch (e) {
          toast.error("Voice failed", { description: e?.response?.data?.detail || "Try again." });
        } finally {
          setProcessing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone blocked", { description: "Allow mic access to talk to ATELIER." });
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  const replay = async (msg) => {
    if (speakingId) {
      audioRef.current?.pause();
      setSpeakingId(null);
      return;
    }
    try {
      setSpeakingId(msg.id);
      const res = await api.speak(msg.text);
      playB64(res.audio_base64, msg.id);
    } catch {
      setSpeakingId(null);
      toast.error("Could not play audio");
    }
  };

  const clearAll = async () => {
    await api.clearHistory();
    setMessages([]);
    toast.success("Conversation cleared");
  };

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] lg:h-screen">
      <header className="px-4 sm:px-8 lg:px-12 pt-8 pb-5 border-b border-white/10 glass sticky top-0 z-20">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="overline-label">The Atelier</div>
            <h2 className="font-serif text-2xl sm:text-3xl font-normal tracking-tight mt-1">
              Design dialogue with ATELIER
            </h2>
          </div>
          <button
            data-testid="clear-chat-btn"
            onClick={clearAll}
            className="text-neutral-500 hover:text-red-400 transition-colors p-2"
            title="Clear conversation"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </header>

      <div ref={feedRef} className="flex-1 overflow-y-auto px-4 sm:px-8 lg:px-12 py-8 space-y-6" data-testid="chat-feed">
        {messages.length === 0 && !processing && (
          <div className="max-w-xl mx-auto text-center py-16">
            <h3 className="font-serif text-3xl font-light text-neutral-200">Good to see you, Lutazzi.</h3>
            <p className="text-neutral-400 mt-3 leading-relaxed">
              Speak or type. Share a sketch or reference. Let's shape the collection — the
              silhouettes, the story, and the house you're building.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {["Pitch me a signature silhouette", "Build a color story for drop 01", "What's our brand manifesto?"].map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="text-xs font-mono px-3 py-2 rounded-full border border-white/10 text-neutral-400 hover:border-amber-500/40 hover:text-white transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
            <div className={`max-w-[85%] sm:max-w-[70%] ${m.role === "user" ? "items-end" : "items-start"} flex flex-col gap-1`}>
              <div className="overline-label text-[10px]">
                {m.role === "user" ? "Lutazzi" : "ATELIER"}
              </div>
              {m.image_base64 && (
                <img src={m.image_base64} alt="attachment" className="rounded-lg max-h-56 border border-white/10 mb-1" />
              )}
              <div
                className={`px-5 py-3 rounded-2xl leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#1A1A1F] border border-white/10 text-neutral-100 rounded-br-sm"
                    : "bg-transparent border border-amber-500/20 text-neutral-200 rounded-bl-sm"
                }`}
              >
                {m.text}
              </div>
              {m.role === "assistant" && (
                <button
                  data-testid={`speak-btn-${m.id}`}
                  onClick={() => replay(m)}
                  className="self-start text-neutral-500 hover:text-amber-400 transition-colors mt-1 flex items-center gap-1 text-xs"
                >
                  <Volume2 size={14} className={speakingId === m.id ? "text-amber-400" : ""} />
                  {speakingId === m.id ? "Playing…" : "Hear it"}
                </button>
              )}
            </div>
          </div>
        ))}

        {(sending || processing) && (
          <div className="flex justify-start" data-testid="typing-indicator">
            <div className="px-5 py-3 rounded-2xl border border-amber-500/20 flex items-center gap-3 text-neutral-400">
              {processing ? <Waveform /> : <Loader2 className="animate-spin text-amber-400" size={16} />}
              <span className="text-sm">{processing ? "Listening & composing…" : "ATELIER is sketching a reply…"}</span>
            </div>
          </div>
        )}
      </div>

      {/* composer */}
      <div className="px-4 sm:px-8 lg:px-12 py-5 border-t border-white/10 glass">
        {attach && (
          <div className="mb-3 inline-flex items-center gap-2 bg-[#1A1A1F] border border-white/10 rounded-lg p-2">
            <img src={attach.preview} alt="to send" className="h-12 w-12 object-cover rounded" />
            <span className="text-xs text-neutral-400">Reference attached</span>
            <button onClick={() => setAttach(null)} className="text-neutral-500 hover:text-red-400" data-testid="remove-attach-btn">
              <X size={15} />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2 sm:gap-3">
          <label
            className="cursor-pointer p-3 rounded-full border border-white/10 text-neutral-400 hover:text-amber-400 hover:border-amber-500/40 transition-colors"
            title="Attach a reference image"
            data-testid="attach-image-label"
          >
            <Paperclip size={18} />
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="chat-image-input" />
          </label>

          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
            placeholder="Describe a look, a mood, a plan…  (Shift+Enter for a new line)"
            className="flex-1 resize-none bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-2xl px-5 py-3 text-neutral-100 placeholder:text-neutral-600 outline-none max-h-40"
          />

          <button
            data-testid="voice-mic-button"
            onClick={recording ? stopRecording : startRecording}
            disabled={processing}
            className={`p-3.5 rounded-full transition-colors ${
              recording
                ? "voice-recording bg-amber-500 text-black"
                : "border border-white/10 text-neutral-300 hover:text-amber-400 hover:border-amber-500/40"
            }`}
            title={recording ? "Stop & send" : "Hold a voice conversation"}
          >
            {recording ? <Square size={18} /> : <Mic size={18} />}
          </button>

          <button
            data-testid="send-btn"
            onClick={handleSend}
            disabled={sending || (!input.trim() && !attach)}
            className="btn-gold p-3.5 rounded-full"
          >
            <Send size={18} />
          </button>
        </div>
        <p className="text-[11px] text-neutral-600 mt-2 text-center font-mono">
          {recording ? "Recording… tap the square to send to ATELIER" : "Type, attach a reference, or tap the mic to talk"}
        </p>
      </div>
    </div>
  );
}
